/**
 * Group chats ("Spjall"): one for the whole choir and one per singing voice. Who may read and write
 * where is decided here, for the server (every chat route) and the client (lists, labels) alike.
 *
 *  - 'choir' (Kórspjall): every member who can sign in (status active / leave), whatever the section.
 *  - 'T1' 'T2' 'B1' 'B2' (raddspjall): members of that voice — their OWN chat.
 *  - The conductor (section 'conductor') and editors / admins (role) may also read, write and moderate
 *    in every voice chat. Those chats are theirs by access, not by voice: the conductor is notified about
 *    them all; an editor / admin is not (they would get every voice's chatter) unless they turn it on.
 *  - Musicians and members without a section have the choir chat only.
 *
 * Membership is never stored: it follows the member's current section, role and status, so a member
 * who changes voice leaves the old voice chat at once, and inactive / erased members cannot sign in.
 * Chat ids are plain TEXT in D1 (no CHECK constraint), so more chats can be added later without a
 * table rebuild.
 */
import { roleAtLeast, SINGING_SECTIONS, type AudienceSubject, type Section } from './audience';

export const VOICE_CHATS = SINGING_SECTIONS;
export type VoiceChatId = (typeof VOICE_CHATS)[number];
export const CHAT_IDS = ['choir', ...VOICE_CHATS] as const;
export type ChatId = (typeof CHAT_IDS)[number];

/** Longest message (same as event comments). */
export const CHAT_MESSAGE_MAX = 2000;

export function isChatId(v: unknown): v is ChatId {
  return typeof v === 'string' && (CHAT_IDS as readonly string[]).includes(v);
}

export function isVoiceChat(id: ChatId): id is VoiceChatId {
  return id !== 'choir';
}

/** May this user read every voice chat (conductor, editors, admins)? */
export function seesAllVoiceChats(user: AudienceSubject): boolean {
  return user.section === 'conductor' || roleAtLeast(user.role, 'editor');
}

/** The voice chat that is the user's own (their singing section), if any. */
export function ownVoiceChat(section: Section | null): VoiceChatId | null {
  return section && (VOICE_CHATS as readonly string[]).includes(section) ? (section as VoiceChatId) : null;
}

export interface ChatAccess {
  id: ChatId;
  /** The choir chat or the user's own voice; false = a voice chat reached through the conductor / editor role. */
  own: boolean;
}

/** Chats the user may read and write, in display order (choir chat first, then the voices). */
export function chatsFor(user: AudienceSubject): ChatAccess[] {
  const own = ownVoiceChat(user.section);
  const out: ChatAccess[] = [{ id: 'choir', own: true }];
  const all = seesAllVoiceChats(user);
  for (const v of VOICE_CHATS) if (v === own || all) out.push({ id: v, own: v === own });
  return out;
}

export function canAccessChat(user: AudienceSubject, id: ChatId): boolean {
  return id === 'choir' || ownVoiceChat(user.section) === id || seesAllVoiceChats(user);
}

/**
 * Muted unless the member chose otherwise: notifications (bell, app badge) come from the choir chat and
 * the member's own voice; the conductor also gets every voice chat. Editors / admins with access to other
 * voices see those chats' unread counts in the chat list but are not notified until they turn it on.
 */
export function defaultMuted(user: AudienceSubject, id: ChatId): boolean {
  if (id === 'choir' || ownVoiceChat(user.section) === id) return false;
  return user.section !== 'conductor';
}

/** The stored choice (chat_reads.muted, NULL = never chosen) or the default. */
export function isMuted(user: AudienceSubject, id: ChatId, stored: number | null | undefined): boolean {
  return stored === 0 || stored === 1 ? stored === 1 : defaultMuted(user, id);
}
